import React, { useRef, useState, useEffect } from "react";
import { useLazyQuery, useQuery, useReactiveVar } from "@apollo/client";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Line } from "react-chartjs-2";
import { DateTime } from "luxon";

import { lang } from "../../../middleware/lang";
import { formatNumber } from "../../../middleware/common-functions";
import { activeBranchVar } from "../../../middleware/cache";
import {
  GET_CONNECT_BALANCE,
  GET_ONLINE_PAYMENT_SUMMARY,
  GetConnectBalanceResponse,
  GetOnlinePaymentSummaryResponse,
} from "./queries";

const todayDateTime = DateTime.local();

const Summary: React.FC = () => {
  const activeBranch = useReactiveVar(activeBranchVar);
  const [showCalendar, setShow] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const [startDate, setStartDate] = useState<Date>(
    todayDateTime.minus({ days: 30 }).toJSDate()
  );
  const [endDate, setEndDate] = useState<Date>(new Date());

  const { data: balanceData } = useQuery<GetConnectBalanceResponse>(
    GET_CONNECT_BALANCE,
    { fetchPolicy: "cache-and-network" }
  );

  const [getSummary, { data: summaryData, loading }] =
    useLazyQuery<GetOnlinePaymentSummaryResponse>(GET_ONLINE_PAYMENT_SUMMARY, {
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    getSummary({
      variables: {
        startDate: DateTime.fromJSDate(startDate).toISODate(),
        endDate: DateTime.fromJSDate(endDate).toISODate(),
        branch_id: +activeBranch || undefined,
      },
    });
    // eslint-disable-next-line
  }, [startDate, endDate, activeBranch]);

  const balance = balanceData?.getConnectBalance;
  const summary = summaryData?.getOnlinePaymentSummary;

  const onChangeDates = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    if (end) setEndDate(end);
    if (start && end) setShow(false);
  };

  function formatDateShort(d: Date): string {
    return d.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  const dateLabel = (() => {
    const s = formatDateShort(startDate);
    const e = formatDateShort(endDate);
    if (s === e) return s;
    return `${s} - ${e}`;
  })();

  // Chart data
  const chartData = {
    labels: summary?.dailyBreakdown.map((d) => d.date) || [],
    datasets: [
      {
        label: lang("Ingresos", "Revenue"),
        data: summary?.dailyBreakdown.map((d) => d.total) || [],
        borderColor: "#FFBB28",
        backgroundColor: "rgba(255, 187, 40, 0.1)",
        fill: true,
        tension: 0.3,
      },
      {
        label: lang("Neto", "Net"),
        data: summary?.dailyBreakdown.map((d) => d.net) || [],
        borderColor: "#48BB78",
        backgroundColor: "rgba(72, 187, 120, 0.1)",
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" as const },
    },
    scales: {
      y: {
        ticks: {
          callback: (value: any) => `$${formatNumber(value)}`,
        },
      },
    },
  };

  return (
    <div>
      {/* Date picker */}
      <div className="sales-overview-header" style={{ marginBottom: "1.5rem" }}>
        <h5 style={{ fontWeight: 700, marginBottom: 0 }}>
          {lang("Resumen de Pagos", "Payment Summary")}
        </h5>
        <div className="sales-date-picker-wrap" ref={pickerRef}>
          <button className="sales-date-btn" onClick={() => setShow(!showCalendar)}>
            <i className="far fa-calendar-alt"></i>
            <span>{dateLabel}</span>
            <i className={`fas fa-chevron-${showCalendar ? "up" : "down"} fa-xs`}></i>
          </button>
          {showCalendar && (
            <div className="sales-date-dropdown">
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={(dates: any) => onChangeDates(dates)}
                selectsRange
                inline
              />
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-center py-3">
          <span className="spinner-border spinner-border-sm" />
        </div>
      )}

      {/* KPI Cards */}
      <div className="kpi-row" style={{ marginBottom: "1.5rem" }}>
        {/* Balance cards */}
        {balance && (
          <>
            <div className="kpi-card kpi-card--hero">
              <span className="kpi-card__label">
                {lang("Saldo Disponible", "Available Balance")}
              </span>
              <span className="kpi-card__value kpi-card__value--accent">
                ${formatNumber(balance.available)}
              </span>
              <div className="kpi-card__footer">
                <span className="kpi-card__sub">MXN</span>
              </div>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__label">
                {lang("Saldo Pendiente", "Pending Balance")}
              </span>
              <span className="kpi-card__value">
                ${formatNumber(balance.pending)}
              </span>
              <div className="kpi-card__footer">
                <span className="kpi-card__sub">
                  {lang("En transito", "In transit")}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Summary KPIs */}
        {summary && (
          <>
            <div className="kpi-card">
              <span className="kpi-card__label">
                {lang("Ingresos Online", "Online Revenue")}
              </span>
              <span className="kpi-card__value">
                ${formatNumber(summary.totalRevenue)}
              </span>
              <div className="kpi-card__footer">
                <span className="kpi-card__sub">
                  {summary.totalOrders} {lang("ordenes", "orders")}
                </span>
              </div>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__label">
                {lang("Comisiones", "Fees")}
              </span>
              <span className="kpi-card__value kpi-card__value--danger">
                ${formatNumber(summary.totalFees)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Revenue chart */}
      {summary && summary.dailyBreakdown.length > 1 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ebebeb",
            borderRadius: 12,
            padding: "1.5rem",
          }}
        >
          <Line type="line" data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
};

export default Summary;
